import { computed, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
// Import core translations
import enCore from '../../translations/en.json'
import deCore from '../../translations/de.json'
// Import page translations
import enPages from '../../translations/pages/en.json'
import dePages from '../../translations/pages/de.json'
// Import blog translations
import enBlog from '../../translations/blog/en.json'
import deBlog from '../../translations/blog/de.json'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationValue = string | string[] | Record<string, any>[] | TranslationData

interface TranslationData {
    [key: string]: TranslationValue
}

// Deep merge function to combine translation objects
function deepMerge(...objects: TranslationData[]): TranslationData {
    const result: TranslationData = {}

    for (const obj of objects) {
        for (const key of Object.keys(obj)) {
            const value = obj[key]
            if (
                value &&
                typeof value === 'object' &&
                !Array.isArray(value) &&
                result[key] &&
                typeof result[key] === 'object' &&
                !Array.isArray(result[key])
            ) {
                result[key] = deepMerge(result[key] as TranslationData, value as TranslationData)
            } else {
                result[key] = value
            }
        }
    }

    return result
}

// Merge all translation files for each language
const mergedEnTranslations = deepMerge(
    enCore as TranslationData,
    enPages as TranslationData,
    enBlog as TranslationData
)

const mergedDeTranslations = deepMerge(
    deCore as TranslationData,
    dePages as TranslationData,
    deBlog as TranslationData
)

// Language configuration - single source of truth
export const SUPPORTED_LANGUAGES = ['en', 'de'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]
export const DEFAULT_LANGUAGE: Language = 'en'
const STORAGE_KEY = 'preferred-language'

/**
 * Detects the preferred language for redirects.
 * Priority: 1) localStorage (user's previous choice), 2) browser language, 3) default 'en'
 *
 * This is the single source of truth for determining which language to use
 * when redirecting from paths without a locale prefix.
 */
export function getPreferredLanguage(): Language {
    // Check localStorage first (respects returning users' explicit choice)
    if (typeof localStorage !== 'undefined') {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored && SUPPORTED_LANGUAGES.includes(stored as Language)) {
            return stored as Language
        }
    }

    // Fall back to browser language detection
    if (typeof navigator !== 'undefined') {
        const browserLang = navigator.language?.toLowerCase() ?? ''
        for (const lang of SUPPORTED_LANGUAGES) {
            if (browserLang.startsWith(lang)) {
                return lang
            }
        }
    }

    return DEFAULT_LANGUAGE
}

// Initialize with preferred language
const currentLanguage = ref<Language>(getPreferredLanguage())

// Initialize translations synchronously - they're bundled with the app
const translations = reactive<Record<Language, TranslationData>>({
    en: mergedEnTranslations,
    de: mergedDeTranslations,
})

function getNestedProperty(obj: TranslationData, path: string): TranslationValue | undefined {
    return path.split('.').reduce<TranslationValue | undefined>(
        (current, key) => {
            if (current && typeof current === 'object') {
                // Handle array access with numeric index
                if (Array.isArray(current)) {
                    const index = parseInt(key, 10)
                    if (!isNaN(index) && index >= 0 && index < current.length) {
                        return current[index] as TranslationValue
                    }
                    return undefined
                }
                // Handle object access
                if (key in current) {
                    return (current as TranslationData)[key]
                }
            }
            return undefined
        },
        obj as TranslationValue | undefined
    )
}

// Translations are loaded synchronously at module initialization
// This function is kept for API compatibility but is now a no-op
function setTranslationLanguage(_language: Language): void {
    // No-op: translations are already loaded synchronously
}

export function useTranslations() {
    // Kept for API compatibility - translations are loaded synchronously at import
    const initTranslations = () => {
        // No-op: translations are bundled and available immediately
    }

    // Derive language from route path for SSG correctness.
    // During SSG, the module-level currentLanguage ref is shared across all route renders,
    // so the last rendered language would win for all pages. By reading the route path directly,
    // each page gets translations in the correct language.
    let route: ReturnType<typeof useRoute> | undefined
    try {
        route = useRoute()
    } catch {
        // Not in a component setup context (e.g., called from tests or module scope)
    }

    const effectiveLanguage = computed(() => {
        if (route?.path) {
            const match = route.path.match(/^\/(en|de)/)
            if (match) {
                return match[1] as Language
            }
        }
        return currentLanguage.value
    })

    const currentTranslations = computed(() => {
        return translations[effectiveLanguage.value] || {}
    })

    const fallbackTranslations = computed(() => {
        return translations.en || {}
    })

    const t = (
        key: string,
        params?: Record<string, string> | string,
        fallback?: string
    ): string => {
        let actualParams: Record<string, string> | undefined
        let actualFallback: string | undefined

        if (typeof params === 'string') {
            actualFallback = params
        } else {
            actualParams = params
            actualFallback = fallback
        }

        let translation = getNestedProperty(currentTranslations.value, key)

        if (!translation && currentLanguage.value !== 'en') {
            translation = getNestedProperty(fallbackTranslations.value, key)
        }

        // t() is for string translations only
        const translationStr = typeof translation === 'string' ? translation : undefined
        let result = translationStr ?? actualFallback ?? key

        if (actualParams) {
            Object.entries(actualParams).forEach(([placeholder, value]) => {
                result = result.replace(`{${placeholder}}`, value)
            })
        }

        return result
    }

    const get = <T = unknown>(key: string): T | undefined => {
        const value = getNestedProperty(currentTranslations.value, key)
        if (value !== undefined) {
            return value as T
        }
        if (currentLanguage.value !== 'en') {
            const fallbackValue = getNestedProperty(fallbackTranslations.value, key)
            if (fallbackValue !== undefined) {
                return fallbackValue as T
            }
        }
        return undefined
    }

    const setLanguage = (language: Language) => {
        currentLanguage.value = language
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(STORAGE_KEY, language)
        }
        setTranslationLanguage(language)
    }

    const availableLanguages = computed(() => SUPPORTED_LANGUAGES)

    return {
        currentLanguage: effectiveLanguage,
        availableLanguages,
        t,
        get,
        setLanguage,
        initTranslations,
    }
}

// Translations are now loaded synchronously at import time
// No need for async initialisation
